import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const TreatAnimation = ({ treat, angelPetPosition, onAnimationComplete }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [sparkles, setSparkles] = useState([]);

  useEffect(() => {
    // Generate sparkle particles for the trail
    const generateSparkles = () => {
      const newSparkles = [];
      for (let i = 0; i < 8; i++) {
        newSparkles.push({
          id: i,
          delay: i * 0.1,
          size: Math.random() * 8 + 4
        });
      }
      setSparkles(newSparkles);
    };

    generateSparkles();
  }, []);

  // Calculate the arc path from bottom center to angel pet
  const startPosition = { x: 50, y: 85 }; // Bottom center of screen (percentage)
  const endPosition = angelPetPosition || { x: 50, y: 40 }; // Angel pet position

  // Create a curved path (quadratic bezier-like)
  const controlPoint = {
    x: (startPosition.x + endPosition.x) / 2 + 15, // Offset for arc
    y: Math.min(startPosition.y, endPosition.y) - 20 // Peak of arc
  };

  const treatMotionVariants = {
    start: {
      x: `${startPosition.x}%`,
      y: `${startPosition.y}%`,
      scale: 1,
      opacity: 1,
      rotate: 0
    },
    flying: {
      x: [`${startPosition.x}%`, `${controlPoint.x}%`, `${endPosition.x}%`],
      y: [`${startPosition.y}%`, `${controlPoint.y}%`, `${endPosition.y}%`],
      scale: [1, 1.2, 0.8],
      opacity: [1, 1, 0.8],
      rotate: [0, 180, 360],
      transition: {
        duration: 1.5,
        ease: "easeInOut",
        times: [0, 0.5, 1]
      }
    },
    impact: {
      scale: 0,
      opacity: 0,
      transition: {
        duration: 0.2
      }
    }
  };

  const sparkleVariants = {
    start: { opacity: 0, scale: 0 },
    trail: {
      opacity: [0, 1, 0],
      scale: [0, 1, 0],
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const impactSparkleVariants = {
    hidden: { opacity: 0, scale: 0 },
    burst: {
      opacity: [0, 1, 0],
      scale: [0, 1.5, 0],
      x: [0, Math.random() * 60 - 30],
      y: [0, Math.random() * 60 - 30],
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      onAnimationComplete();
    }, 2000); // Total animation duration

    return () => clearTimeout(timer);
  }, [onAnimationComplete]);

  return (
    <AnimatePresence>
      {isVisible && (
        <div className="fixed inset-0 pointer-events-none z-40">
          {/* Flying Treat */}
          <motion.div
            className="absolute w-12 h-12 flex items-center justify-center text-3xl"
            style={{
              left: 0,
              top: 0,
              transform: 'translate(-50%, -50%)'
            }}
            variants={treatMotionVariants}
            initial="start"
            animate={["flying", "impact"]}
          >
            {treat.icon}
          </motion.div>

          {/* Sparkle Trail */}
          {sparkles.map((sparkle) => (
            <motion.div
              key={sparkle.id}
              className="absolute text-yellow-300"
              style={{
                left: `${startPosition.x}%`,
                top: `${startPosition.y}%`,
                fontSize: `${sparkle.size}px`,
                transform: 'translate(-50%, -50%)'
              }}
              variants={sparkleVariants}
              initial="start"
              animate="trail"
              transition={{
                delay: sparkle.delay,
                duration: 0.8
              }}
            >
              ✨
            </motion.div>
          ))}

          {/* Impact Effect Sparkles */}
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={`impact-${i}`}
              className="absolute text-purple-300"
              style={{
                left: `${endPosition.x}%`,
                top: `${endPosition.y}%`,
                fontSize: '16px',
                transform: 'translate(-50%, -50%)'
              }}
              variants={impactSparkleVariants}
              initial="hidden"
              animate="burst"
              transition={{
                delay: 1.3 + i * 0.1,
                duration: 0.6
              }}
            >
              ⭐
            </motion.div>
          ))}
        </div>
      )}
    </AnimatePresence>
  );
};

export default TreatAnimation;
